import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  AppBar,
  Toolbar,
  IconButton,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
  Chip,
} from '@mui/material';
import {
  ChatOutlined as ChatIcon,
  Menu as MenuIcon,
  AutoAwesome as PyrenzPlusIcon,
  AccountBalanceWallet as WalletIcon,
} from '@mui/icons-material';
import { FaDiscord } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Utils } from '@client/utility';
import { PyrenzMenu, PyrenzBlueButtonWithLoading } from '@client/theme';
import { useUserStore } from '@client/store';

interface BalanceResponse {
  status: string;
  balance: number;
}

const fetchBalanceAPI = async (userUUID: string): Promise<number> => {
  const res = await Utils.post<BalanceResponse>('/webhook/balance', {
    user_uuid: userUUID
  });

  if (res.status !== 'success') {
    throw new Error('Failed to load balance');
  }

  return res.balance;
};

const tiltVariants = {
  hover: { rotate: [0, 15, -10, 10, 0], transition: { duration: 0.4 } },
};

function LogoText() {
  const navigate = useNavigate();
  const text = 'PyrenzAI';
  return (
    <motion.nav
      style={{ display: 'flex', cursor: 'pointer', gap: '0.5rem' }}
      onClick={() => navigate('/')}
    >
      {text.split('').map((letter, idx) => (
        <motion.span
          key={idx}
          variants={tiltVariants}
          whileHover="hover"
          style={{
            fontWeight: 'bold',
            fontSize: '1.6rem',
            color: idx >= text.length - 2 ? '#3f51b5' : 'inherit',
            textShadow: idx >= text.length - 2 ? '0 0 8px #3f51b5' : 'none',
            display: 'inline-block',
          }}
        >
          {letter}
        </motion.span>
      ))}
    </motion.nav>
  );
}

export function PreviewHeader() {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [displayCredits, setDisplayCredits] = useState<number | null>(null);
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMdOrSmaller = useMediaQuery(theme.breakpoints.down('md'));
  const tier = useUserStore((state) => state.tier);
  const credits = useUserStore((state) => state.credits);
  const is_login = useUserStore((state) => state.is_login);
  const userUUID = useUserStore((state) => state.userUUID);
  const setCredits = useUserStore((state) => state.setCredits);

  useEffect(() => {
    const fetchBalance = async () => {
      if (!is_login || !userUUID) {
        setDisplayCredits(null);
        return;
      }
      try {
        const balance = await fetchBalanceAPI(userUUID);
        setDisplayCredits(balance);
        setCredits(balance);
      } catch (err) {
        console.error('Failed to fetch balance:', err);
        setDisplayCredits(credits);
      }
    };
    fetchBalance();
  }, [is_login, userUUID, credits, setCredits]);

  const menuItems = [
    { name: t('navigation.chats'), icon: <ChatIcon />, link: '/Archive' },
    {
      name: t('footer.discord'),
      icon: <FaDiscord />,
      link: 'https://discord.gg/zTcyP4WB8h',
      external: true,
    },
  ];

  const isPaidTier = tier && tier !== 'FREE' && tier !== 'free';
  if (isPaidTier) {
    menuItems.push({
      name: 'Pyrenz+',
      icon: <PyrenzPlusIcon />,
      link: '/Credits',
    });
  }

  const showCredits = is_login && displayCredits !== null;

  return (
    <AppBar
      component="header"
      position="sticky"
      elevation={0}
      sx={{
        top: 0,
        zIndex: theme.zIndex.appBar,
        backgroundColor: 'transparent',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            px: 2,
          }}
        >
          <Box ml={{ lg: '50px', xs: '15px' }}>
            <LogoText />
          </Box>

          {!isMdOrSmaller && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {showCredits && (
                <Chip
                  icon={<WalletIcon />}
                  label={`${displayCredits} Credits`}
                  onClick={() => navigate('/Credits')}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.1)',
                    color: 'white',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                  }}
                />
              )}
              {menuItems.map(({ name, icon, link, external }) => (
                <PyrenzBlueButtonWithLoading
                  key={name}
                  startIcon={icon}
                  onClick={() =>
                    external ? window.open(link, '_blank') : navigate(link)
                  }
                >
                  {name}
                </PyrenzBlueButtonWithLoading>
              ))}
            </Box>
          )}

          {isMdOrSmaller && (
            <>
              <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
                <MenuIcon sx={{ color: theme.palette.text.primary }} />
              </IconButton>
              <PyrenzMenu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
              >
                {showCredits && (
                  <MenuItem onClick={() => navigate('/Credits')}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WalletIcon />
                      <Typography>{displayCredits} Credits</Typography>
                    </Box>
                  </MenuItem>
                )}
                {menuItems.map(({ name, icon, link, external }) => (
                  <MenuItem
                    key={name}
                    onClick={() => {
                      setMenuAnchor(null);
                      external ? window.open(link, '_blank') : navigate(link);
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {icon}
                      <Typography>{name}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </PyrenzMenu>
            </>
          )}
        </Toolbar>
      </motion.div>
    </AppBar>
  );
}